/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import dayjs from 'dayjs'
import uuid from 'uuid'
import { Button } from '@styles/button.style'
import * as Utils from '@utils/utils'
import { formatCreatedAtToElapsedTime, makeAttachmentDetailObjToBettingRecordTrack } from '@utils/utils'
import _ from 'lodash'
import SimpleBettingHistory from '@components/SimpleBettingHistory'
import {
  ATTACHMENT_TYPE,
  COMMUNICATION_BLOCK_TYPE,
  FROM_VIEW_TYPE,
  GlobalStoreKey,
  USER_COMMENT_TYPE,
  USER_REPORT_TYPE,
} from '@v2/constants/common'
import useRootStore from '@store/useRootStore'
import { makeATagAutoGenerationElements } from '@utils/jsxutils'
import { LikeButton } from '@components/Button/LikeButton'
import { AdvButton } from '@components/Button'
import { UserPickContentsStyle, UserPickPopoverWrap, UserPickWrap } from '@views/Home/index.style'
import { SPORT_ID_GRAY_ICON_MAP } from '@shared/SportIdGroup/Gray'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'
import PopupUtils from '@utils/popup-utils'
import { UserPickSpecialMemberMark } from '@views/Home/UserPick/Components/UserPickSpecialMemberMark'
import { Schema$CommentMatchInfo, Schema$UserComment, Schema$UserCommentExt } from '@interface/comment'
import DialogUtils from '@utils/dialog-utils'
import { Popover } from 'antd'
import CommentAddImg from '@assets/images/btn/comment-add-btn.png'
import { AdvCommentEditor } from '@components/AdvComment/AdvCommentEditor'
import { runInAction } from 'mobx'
import { ArrowIco } from '@shared/Icon/ArrowIcon'
import AdvImg from '@components/AdvImg'

interface AdvCommentListItemProps {
  userComment: Schema$UserCommentExt
  fromViewType: FROM_VIEW_TYPE
  canMyCommentModify?: boolean
  isDisabledLikeButton?: boolean
  showMatchInfo?: boolean
  callbackDeleted?: (comment: Schema$UserComment) => void
  callbackCreated?: (comment: Schema$UserComment) => void
  callbackUpdated?: (comment: Schema$UserComment) => void
  callbackLiked?: (comment: Schema$UserComment) => void
  hideReply?: boolean
  toggleEditor?: (comment: Schema$UserCommentExt) => void
  disableUserDetailInfo?: boolean
  onUserBlockSuccessHandler?: (userPromiseId: string, blockTypes: COMMUNICATION_BLOCK_TYPE[]) => void
  checkCommentTargetEnabled?: boolean
}

const LIKE_ACTIVE_CLASS = 'active'

export const AdvCommentListItem: React.FC<AdvCommentListItemProps> = observer((
  props: AdvCommentListItemProps,
): React.ReactElement => {
  const {
    userComment, canMyCommentModify = true, isDisabledLikeButton = false,
    fromViewType, showMatchInfo = false, disableUserDetailInfo = false,
    hideReply = false, toggleEditor,
    callbackDeleted, callbackCreated, callbackUpdated, callbackLiked, onUserBlockSuccessHandler,
    checkCommentTargetEnabled = false,
  } = props
  const {
    isLike, likeCount, visibleEditor, isMine,
    matchInfo = {} as Schema$CommentMatchInfo, attachmentType, targetId,
  } = userComment
  const { dateOfMatch, homeTeamName, awayTeamName, sportId } = matchInfo

  const { globalStore, commentStore, userPickStore, companyPickStore } = useRootStore()

  const [addSuffixList, setAddSuffixList] = useState('[]')
  const [bubbleVisible, setBubbleVisible] = useState(false)
  const [contentElements, setContentElements] = useState<React.ReactElement[]>()

  const attachImgUrlPrefix = globalStore.findGlobalSetting(
    GlobalStoreKey.USER_COMMENT_ATTACH_IMAGE_URL_PREFIX,
  )?.value
  const attachEmoticonUrlPrefix = globalStore.findGlobalSetting(GlobalStoreKey.CHAT_EMOTICON_URL_PREFIX)?.value

  const onClickViewImg = (e, imageSrc: string): void => {
    e.stopPropagation()
    DialogUtils.showImagePreview({ imageSrc, imageAlt: '댓글 업로드 이미지' })
  }

  const addEmoticonSuffixHandler = (commentId: string): void => {
    const suffixObj = JSON.parse(addSuffixList)
    let suffix = _.find(suffixObj, { commentId })
    if (suffix) {
      suffix.suffix = uuid.v4()
      _.remove(suffixObj, { commentId })
    } else {
      suffix = { commentId, suffix: uuid.v4() }
    }
    suffixObj.push(suffix)
    setAddSuffixList(JSON.stringify(suffixObj))
  }

  React.useEffect(() => {
    if (attachmentType === ATTACHMENT_TYPE.EMOTICON) {
      const commentId = userComment.id
      addEmoticonSuffixHandler(commentId)
    }
  }, [attachmentType, userComment.id])

  const onClickOpenUserBettingListInfo = (e, betId: string, nick: string): void => {
    e.stopPropagation()
    DialogUtils.showSharedBettingList({ betId, fromUserNick: nick })
  }

  const onClickOpenUserBettingHistory = async (e) => {
    await onClickOpenUserBettingListInfo(e, userComment.attachment, userComment.commenter.userNick)
  }

  const onClickEditComment = (): void => {
    setBubbleVisible(false)
    const popupId = 'adv-comment-list-item-editor-popup'
    const className = 'edit-comments'

    let commentTypeText
    if (userComment.commentDepth > 0) {
      commentTypeText = '답글'
    } else {
      commentTypeText = '댓글'
    }
    if (userComment.commentType === USER_COMMENT_TYPE.USER_PICK_COMMENT) {
      commentTypeText = `나도승부사 ${commentTypeText}`
    } else if (userComment.commentType === USER_COMMENT_TYPE.COMPANY_PICK_COMMENT) {
      commentTypeText = `프리미엄 분석가 ${commentTypeText}`
    }
    PopupUtils.showAdvCommentEditor({
      popupId,
      className,
      commentTypeText,
      fromViewType,
      commentProp: userComment,
      callbackCreated,
      callbackUpdated,
      callbackClose: () => {
        globalStore.closeGeneralPopup(popupId)
      },
    })
  }

  const onClickDeleteComment = (titleSuffix = '댓글'): void => {
    setBubbleVisible(false)
    DialogUtils.showDeleteComment({
      title: `${titleSuffix} 삭제`,
      callbackPositive: () => {
        commentStore.deleteComment(userComment).then(result => {
          if (result && callbackDeleted) {
            callbackDeleted(result)
          }
          Utils.showToast(Utils.TOAST_TYPE.INFO, '삭제되었습니다.')
        })
      },
    })
  }

  const imageAttachmentComponent = (): React.ReactElement => {
    const attachment = userComment.attachment
    const attachImgSrc = `${attachImgUrlPrefix}${attachment}`
    return (
      <div className="upload-preview uploaded-img">
        <Button TP onClick={(e) => onClickViewImg(e, attachImgSrc)} className="attachment-img-btn">
          <AdvImg src={attachImgSrc} srcMode={'small'} className="upload-img-preview lhn1" alt="이미지 미리보기"/>
        </Button>
      </div>
    )
  }
  const emoticonAttachmentComponent = (): React.ReactElement => {
    const attachment = userComment.attachment
    const commentId = userComment.id
    const suffixObj = JSON.parse(addSuffixList)
    const suffix = _.find(suffixObj, { commentId })
    const attachEmoticonSrc = `${attachEmoticonUrlPrefix}${attachment}?i=${commentId}&t=${suffix ? suffix.suffix : uuid.v4()}`
    return (
      <div className="upload-preview">
        <img
          src={attachEmoticonSrc}
          alt="프리미엄 분석가 댓글 이모티콘"
          className="upload-img-preview emoticon"
          onClick={(e) => {
            e.stopPropagation()
            addEmoticonSuffixHandler(commentId)
          }}
        />
      </div>
    )
  }
  const betHistoryAttachmentComponent = (): React.ReactElement => {
    const AttachmentDetailObj = userComment.attachmentDetail ? JSON.parse(userComment.attachmentDetail) : {}
    const bettingRecordTrack = makeAttachmentDetailObjToBettingRecordTrack(AttachmentDetailObj)
    return (
      <div className="upload-preview">
        <SimpleBettingHistory
          bettingRecordTrack={bettingRecordTrack}
          onClickOpenBettingListInfo={onClickOpenUserBettingHistory}
        />
      </div>
    )
  }

  let attachmentComponent
  switch (attachmentType) {
    case ATTACHMENT_TYPE.IMAGE:
      attachmentComponent = imageAttachmentComponent()
      break
    case ATTACHMENT_TYPE.EMOTICON:
      attachmentComponent = emoticonAttachmentComponent()
      break
    case ATTACHMENT_TYPE.BET_HISTORY:
      attachmentComponent = betHistoryAttachmentComponent()
      break
    default:
      break
  }

  const modifiedAt = formatCreatedAtToElapsedTime({
    createdAt: userComment.createdAt,
    modifiedAt: userComment.lastModifiedAt,
    }
  )

  let content = ''
  if (userComment.filteredContent) {
    content = userComment.filteredContent
  } else {
    content = userComment.content
  }

  const onClickCommentLike = async () => {
    const result = await commentStore.toggleCommentLike(userComment)
    if (callbackLiked) {
      callbackLiked(result)
    }
  }

  // TODO
  const [isShowMore, setIsShowMore] = useState(false)
  const textLimit = useRef<number>(170)

  const row = content.split('\n').length

  // 170자가 넘어가거나 5줄이 넘어가는 경우
  const isOverText = (content.length > textLimit.current) || row > 5
  const ellipsisVersion = isOverText && !isShowMore

  let shortTextLimitClass = ''

  if (isOverText && !isShowMore) {
    // 총 글자수는 적은데 5줄 이상인 경우
    if ((textLimit.current > _.size(content)) && row > 5) {
      shortTextLimitClass = 'short'
    }
  }

  useEffect(() => {
    let commentContents = content
    if (ellipsisVersion) {
      commentContents = content.slice(0, textLimit.current)
    }
    const generatedElems = makeATagAutoGenerationElements(globalStore, commentContents, false, () => {
      const noEmbeddedElems = makeATagAutoGenerationElements(globalStore, commentContents, true)
      if (userComment.replyToUserNick) {
        const userNick = <span className={'nick-tag'}>@{userComment.replyToUserNick}</span>
        noEmbeddedElems.unshift(userNick)
      }
      setContentElements(noEmbeddedElems)
    })
    if (userComment.replyToUserNick) {
      const userNick = <span className={'nick-tag'}>@{userComment.replyToUserNick}</span>
      generatedElems.unshift(userNick)
    }
    setContentElements(generatedElems)
  }, [content, ellipsisVersion])

  const commentContentElements = useMemo(() => {
    if ((textLimit.current > content.length) && row > 5) {
      // 총 글자수는 적은데 5줄 이상인 경우
      return (
        <Fragment>
          <p className={`comments ${shortTextLimitClass}`}>{contentElements}</p>{ellipsisVersion && '...'}

          {/* ************** 첨부된 파일 영역  ************** */}
          {attachmentComponent && !ellipsisVersion && attachmentComponent}

          {ellipsisVersion && <span className="more-txt">더 보기</span>}
        </Fragment>
      )
    } else {
      return (
        <Fragment>
          <p className="comments">{contentElements}{ellipsisVersion && '...'}</p>

          {/* ************** 첨부된 파일 영역  ************** */}
          {attachmentComponent && !ellipsisVersion && attachmentComponent}

          {ellipsisVersion && <span className="more-txt">더 보기</span>}
        </Fragment>
      )
    }
  }, [ellipsisVersion, contentElements, attachmentComponent])

  const onClickToggleBtn = (e) => {
    const elements = e.currentTarget

    if (isOverText) {
      setIsShowMore(!isShowMore)

      if (_.includes(elements.classList, 'open')) {
        elements.classList.remove('open')
      } else {
        elements.classList.add('open')
      }
    }
  }

  const matchInfoClass = classNames(
    'match-info row simple-info comment-list-match-info',
    { 'pc-info': !isMobile },
  )

  const onClickUserDetailInfoPopup = () => {
    if (!disableUserDetailInfo) {
      PopupUtils.showUserDetailInfo(userComment.commenter.userPromiseId)
    }
  }

  const onClickReportComment = (): void => {
    if (!userComment.reported) {
      setBubbleVisible(false)
      let reportType
      switch (userComment.commentType) {
        case USER_COMMENT_TYPE.COMPANY_PICK_COMMENT:
          reportType = USER_REPORT_TYPE.COMPANY_PICK_COMMENT
          break
        case USER_COMMENT_TYPE.USER_PICK_COMMENT:
          reportType = USER_REPORT_TYPE.USER_PICK_COMMENT
          break
      }
      DialogUtils.showUserReport({
        reportType, targetId: userComment.id, targetUserPromiseId: userComment.commenter.userPromiseId,
        callbackReport: (result) => {
          if (result) {
            runInAction(() => {
              userComment.reported = true
            })
          }
        },
      })
    }
  }

  const isUserBlock = userComment.commenter.isBlocked
  const isCommentBlock = userComment.isBlocked

  const onClickBlockUser = () => {
    const { userPromiseId, userNick, userProfileImg } = userComment.commenter
    if (!isUserBlock) {
      DialogUtils.showUserBlock(userPromiseId, userNick, userProfileImg, onUserBlockSuccessHandler)
    } else {
      DialogUtils.showUserUnBlock(userPromiseId, onUserBlockSuccessHandler)
    }
  }

  const onVisiblePopover = (visible) => {
    setBubbleVisible(visible)
  }

  const commentModelForNewReply: Schema$UserComment = {
    id: null,
    replyToCommentId: userComment.id,
    commentType: userComment.commentType,
    targetId: userComment.targetId,
    commentDepth: userComment.commentDepth + 1,
    replyToUserNick: userComment.commenter.userNick,
  }

  if (userComment.commentDepth === 0) {
    commentModelForNewReply.replyToCommentIdPath = [userComment.id]
    commentModelForNewReply.rootCommentId = userComment.id
  } else {
    commentModelForNewReply.replyToCommentIdPath = userComment.replyToCommentIdPath || []
    commentModelForNewReply.replyToCommentIdPath.push(userComment.id)
    commentModelForNewReply.rootCommentId = userComment.rootCommentId
  }

  const onClickToggleCommentWrite = () => {
    globalStore.clearActivityScoreNoticeQueue()
    if (toggleEditor) {
      toggleEditor(userComment)
    }
  }

  const isReplyExist = !_.isEmpty(userComment.replies) && !hideReply
  const commentItemClass = classNames('pick-comments-list--item', {
    'reply-exist': isReplyExist,
    'deleted': !userComment.enabled,
    'pc': !isMobile,
  })

  const isUserBlockEnabled = globalStore.getUserBlockEnabled()
  const isPossibleUserBlock = globalStore.isPossibleUserBlockByUserPromiseId(
    userComment.commenter.userPromiseId,
  )

  const popoverContent = (
    <UserPickPopoverWrap>
      {isMine &&
        <Fragment>
          {checkCommentTargetEnabled && Boolean(matchInfo.commentTargetEnabled) &&
            (<Button TP className="popover-btn" onClick={onClickEditComment}>수정하기</Button>)
          }
          {!checkCommentTargetEnabled &&
            (<Button TP className="popover-btn" onClick={onClickEditComment}>수정하기</Button>)
          }
          <Button TP className="popover-btn delete" onClick={() => {
            let titleSuffix = '댓글'
            if (userComment.commentDepth > 0) {
              titleSuffix = '답글'
            }
            onClickDeleteComment(titleSuffix)
          }}>삭제하기</Button>
        </Fragment>
      }
      {!isMine && (
        <Fragment>
          <Button TP className="popover-btn" onClick={onClickReportComment}>
            {userComment.reported ? '신고완료' : '신고하기'}
          </Button>
          {isUserBlockEnabled && isPossibleUserBlock && (
            <Button TP className={isUserBlock ? 'popover-btn delete' : 'popover-btn'} onClick={onClickBlockUser}>
              {isUserBlock ? '차단해제' : '차단하기'}
            </Button>
          )}
        </Fragment>
      )}
    </UserPickPopoverWrap>
  )


  let userCommentContents
  if (isCommentBlock) {
    if (userComment.commentDepth > 0) {
      userCommentContents = <div className="block-message">차단된 유저의 답글입니다.</div>
    } else {
      userCommentContents = <div className="block-message">차단된 유저의 댓글입니다.</div>
    }
  } else {
    userCommentContents = (
      <AdvButton
        contents={commentContentElements}
        onClick={onClickToggleBtn}
        css={UserPickContentsStyle}
        className={ellipsisVersion && 'show-more'}
      />
    )
  }

  const advCommentEditor: JSX.Element = useMemo(() => {
    return (
      <AdvCommentEditor
        className={visibleEditor ? '' : 'adv-hidden'}
        key={`adv-comment-editor-reply_${fromViewType}_${userComment.id}`}
        isFocus={visibleEditor}
        placeholder={'답글을 남겨보세요'}
        callbackClose={onClickToggleCommentWrite}
        fromViewType={fromViewType}
        callbackCreated={(createdComment: Schema$UserComment): void => {
          toggleEditor(userComment)
          if (callbackCreated) {
            callbackCreated(createdComment)
          }
        }}
        callbackUpdated={(updatedComment: Schema$UserComment): void => {
          toggleEditor(userComment)
          if (callbackUpdated) {
            callbackUpdated(updatedComment)
          }
        }}
        commentProp={commentModelForNewReply}
      />
    )
  }, [visibleEditor])

  let PickCommentItem
  if (!userComment.enabled) {
    let deleteTxt = '작성자에 의해 삭제되었습니다'
    if (userComment.isAdminDisabled) {
      deleteTxt = '관리자에 의해 삭제되었습니다'
    }
    PickCommentItem = (
      <div key={`user-comment-item-wrapper-${userComment.id}`} className={commentItemClass}>{deleteTxt}</div>
    )
  } else {
    PickCommentItem = (
      <div key={`user-comment-item-wrapper-${userComment.id}`} className={commentItemClass}>
        <Button TP className="pick-comments-profile-btn" onClick={onClickUserDetailInfoPopup}>
          <div className={isUserBlock ? 'profile-img-wrap small blocked lhn23' : 'profile-img-wrap small lhn23'}>
            {userComment.commenter.isCustomProfileImg ?
              (<AdvImg src={userComment.commenter.userProfileImg} srcMode={'small'} alt="사진첩 프로필" className="profile-img custom-img"/>)
              :
              (<AdvImg src={userComment.commenter.userProfileImg} srcMode={'small'} alt="스킨 프로필" className="profile-img"/>)
            }
          </div>
          <UserPickSpecialMemberMark
            showSMark={userComment.commenter.hasSpecialMark}
            isSpecialMember={userComment.commenter.isSpecialMember}
            userTitleId={userComment.commenter.userTitleId}
            userTitleEnabled={userComment.commenter.userTitleEnabled}
            tiny
            darkBorder
          />
        </Button>

        <div className="comments-box">
          <span className={isUserBlock ? 'nickname blocked' : 'nickname'}>
            {userComment.commenter.userNick}
            {/** 분석글 작성자만 "작성자"마크 출력 */}
            {/* TODO 답글에서도 분석글 작성자에게 "작성자"마크가 출력되어야 합니다. */}
            {userComment.commenter.isTargetOwner && <span className="comment-writer-mark">작성자</span>}
            <div id={`CommentPopoverButton_${fromViewType}_${userComment.id}`}
                 style={{ position: 'relative', float: 'right' }}>
              <Popover
                key={`replyPopover_${fromViewType}_${userComment.id}`}
                visible={bubbleVisible}
                onVisibleChange={onVisiblePopover}
                placement="bottomLeft"
                content={popoverContent}
                trigger={isMobile ? 'click' : 'hover'}
                mouseLeaveDelay={0.05}
                overlayClassName={'user-comment-menu'}
                // getPopupContainer={() => document.getElementById(`CommentPopoverButton_${fromViewType}_${userComment.id}`)}
                getPopupContainer={(triggerNode: any) => triggerNode.parentNode} /* body 하위에 div를 생성하지 않고 additional-dividend-mark과 동일 선상에 생성되도록 설정 - body 하위에 생성되면 아이폰에서 모달 뒤에 뜸 */
              >
                <Button TP className="comment-extra-btn">
                  <img src={CommentAddImg} alt="댓글 추가버튼"/>
                </Button>
              </Popover>
            </div>
            <span className="date">{modifiedAt}</span>
          </span>
          {/*<p className="comments">{content}</p>*/}

          {userCommentContents}

          <div className="comment-btn-wrap">
            <LikeButton
              id="advCommentLikeBtn"
              className={isLike ? LIKE_ACTIVE_CLASS : ''}
              onClick={onClickCommentLike}
              count={likeCount}
              disabled={isDisabledLikeButton}
            />
            <Fragment>
              {canMyCommentModify && !hideReply &&
                <Button TP className="reply-btn" onClick={onClickToggleCommentWrite}>답글쓰기</Button>}
              {advCommentEditor}
            </Fragment>
          </div>
        </div>
      </div>
    )
  }

  const onClickShowCompanyPickDetail = async () => {
    if (!matchInfo.commentTargetEnabled) {
      Utils.showToast(Utils.TOAST_TYPE.INFO, '삭제된 분석글 입니다.')
      return
    }

    if (userComment.commentType === USER_COMMENT_TYPE.USER_PICK_COMMENT) { //나도승부사 경기분석 팝업창
      try {
        await userPickStore.fetchMatch(matchInfo.matchId)
        PopupUtils.showUserPickList({ targetUserPickId: targetId, fromView: FROM_VIEW_TYPE.MY_PROFILE_COMMENT })
      } catch (error) {
        console.error(error)
      }
    } else if (userComment.commentType === USER_COMMENT_TYPE.COMPANY_PICK_COMMENT) { //프리미엄 분석가 경기분석 팝업창
      try {
        await companyPickStore.fetchCompanyPick(targetId)
        await companyPickStore.fetchCompanyPickManagers({
          userPromiseIds: [companyPickStore.selectedCompanyPick.userPromiseId],
        })
        PopupUtils.showCompanyPickDetailPopup()
      } catch (error) {
        console.error(error)
      }
    }
  }

  if (showMatchInfo) {
    return (
      <UserPickWrap className="commet-list">
        <Button TP className={matchInfoClass} onClick={() => onClickShowCompanyPickDetail()}>
          {SPORT_ID_GRAY_ICON_MAP[sportId]}
          <span className="date">{dayjs(dateOfMatch).format('MM/DD(ddd) HH:mm')}</span>
          <div className="team-info">
          <span className="team">
            {homeTeamName}
          </span>
            <span className="vs">vs</span>
            <span className="team">
            {awayTeamName}
          </span>
          </div>
          <div className="btn-ico">
            <ArrowIco/>
          </div>
        </Button>
        {PickCommentItem}
      </UserPickWrap>
    )
  }

  return PickCommentItem
})
